import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgxEditorModule, Editor } from 'ngx-editor';
import { AuthService } from '../service/auth/auth.service';
import { AdminRestService } from '../service/rest/admin-rest.service';

@Component({
  selector: 'app-admin-social-templates',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxEditorModule],
  templateUrl: './admin-social-templates.component.html',
  styleUrl: './admin-users.component.css'
})
export class AdminSocialTemplatesComponent implements OnInit {
  sessionId = '';
  content: any[] = [];
  loading = false;
  error = '';
  message = '';
  detailItem: any = null;
  detailForm: any = {};
  detailSaving = false;
  deleteItem: any = null;
  deleteError = '';
  sortColumn = 'triggerKey';
  sortDir: 'asc' | 'desc' = 'asc';
  bodyEditor: Editor | null = null;
  readonly channels = ['push', 'in_app', 'email', 'local'];

  constructor(
    private authService: AuthService,
    private adminRest: AdminRestService
  ) {}

  ngOnInit(): void {
    const user = this.authService.getUser();
    this.sessionId = user?.sid || '';
    void this.load();
  }

  async load(): Promise<void> {
    if (!this.sessionId) return;
    this.loading = true;
    this.error = '';
    try {
      this.content = (await this.adminRest.getAdminSocialTemplates(this.sessionId)) ?? [];
    } catch (e: any) {
      this.error = e?.message || 'Failed to load templates';
    } finally {
      this.loading = false;
    }
  }

  openCreate(): void {
    this.detailItem = { id: null };
    this.detailForm = { triggerKey: '', channel: 'push', headline: '', body: '', active: true };
    this.bodyEditor = new Editor();
  }

  openEdit(item: any): void {
    this.detailItem = item;
    this.detailForm = {
      triggerKey: item.triggerKey ?? '',
      channel: item.channel ?? 'push',
      headline: item.headline ?? '',
      body: item.body ?? '',
      active: !!item.active
    };
    this.bodyEditor = new Editor();
  }

  closeDetail(): void {
    this.detailItem = null;
    if (this.bodyEditor) {
      this.bodyEditor.destroy();
      this.bodyEditor = null;
    }
    void this.load();
  }

  async save(): Promise<void> {
    if (!this.sessionId) return;
    this.detailSaving = true;
    this.error = '';
    try {
      const payload = {
        triggerKey: this.detailForm.triggerKey?.trim(),
        channel: this.detailForm.channel,
        headline: this.detailForm.headline?.trim(),
        body: this.detailForm.body ?? '',
        active: this.detailForm.active ? 1 : 0
      };
      if (!payload.triggerKey || !payload.headline) {
        this.error = 'Trigger key and headline are required';
        return;
      }
      if (this.detailItem?.id == null) {
        await this.adminRest.postAdminSocialTemplate(this.sessionId, payload);
        this.message = 'Template created.';
      } else {
        await this.adminRest.putAdminSocialTemplate(this.sessionId, this.detailItem.id, payload);
        this.message = 'Template updated.';
      }
      this.closeDetail();
    } catch (e: any) {
      this.error = e?.message || 'Save failed';
    } finally {
      this.detailSaving = false;
    }
  }

  openDeleteConfirm(item: any): void {
    this.deleteItem = item;
    this.deleteError = '';
  }

  closeDeleteConfirm(): void {
    this.deleteItem = null;
    this.deleteError = '';
  }

  async confirmDelete(): Promise<void> {
    if (!this.deleteItem?.id || !this.sessionId) return;
    this.deleteError = '';
    try {
      await this.adminRest.deleteAdminSocialTemplate(this.sessionId, this.deleteItem.id);
      this.closeDeleteConfirm();
      await this.load();
    } catch (e: any) {
      this.deleteError = e?.error?.error || e?.message || 'Delete failed';
    }
  }

  get sortedContent(): any[] {
    const col = this.sortColumn;
    const dir = this.sortDir === 'asc' ? 1 : -1;
    return [...this.content].sort((a, b) => {
      const va = (a[col] ?? '') + '';
      const vb = (b[col] ?? '') + '';
      return dir * va.localeCompare(vb, undefined, { sensitivity: 'base' });
    });
  }

  setSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDir = 'asc';
    }
  }

  sortIcon(column: string): string {
    if (this.sortColumn !== column) return 'bi bi-arrow-down-up';
    return this.sortDir === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down';
  }
}
